import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import InfoIcon from "@mui/icons-material/Info";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { Clipboard, ExternalLink } from "react-feather";
import { Link as RouterLink, useParams } from "react-router-dom";
import mainStyles from "styles";

import { BillingDetails } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_pb";
import { ContractStatus } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import {
  ChargifyState,
  PaymentMethod,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import {
  Components,
  SubscriptionType,
  Tier,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { toUUID } from "../../../helpers/s12-helpers";
import ChangeComponentQuantity from "../../org-display/tabs/subscription-actions/change-component-quantity";
import DisplayHeading from "../../shared/display-heading";
import {
  getBillingDetails as getBillingDetailsApi,
  retryPayment as retryPaymentApi,
} from "api/endpoints/subscription";
import { gRPCStatus } from "api/grpc-status-codes";
import { callOptionsHeaders } from "api/helpers";
import ChargifyLogoSvg from "assets/chargify-logo";
import BillingDetailsForm from "components/org-display/details-display-cards/billing-details-form";
import { FEATURE_FLAG_KEY_MANUAL_BILLING_EXPERIENCE } from "constants/billing-experiences";
import { chargifyStateDisplayMapping } from "constants/chargify";
import { ENTERPRISE_PLAN_DOC_LINK } from "constants/contracts";
import {
  DEFAULT_SNACKBAR_DURATION,
  NO_DISPLAY_DATA,
  PillStatusMode,
  pricingPeriodDisplayMapping,
  subscriptionPaymentMethodDisplayMapping,
  subscriptionTierDisplayMapping,
  subscriptionTypeNameDisplayMapping,
} from "constants/index";
import { userSeatsToString } from "constants/seats";
import {
  FeatureFlagsContext,
  UseGetFeatureFlagValue,
} from "context/feature-flags-context";
import {
  ContractsListContext,
  IsOrgAndSeatDataLoadingContext,
  OrgDataContext,
  SubDataContext,
  SubscriptionSeatUsageContext,
} from "context/organisation-context";
import { PaymentMethodsContext } from "context/payment-methods-context";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { mapChargifyURL } from "helpers/env-values-helper";
import { isUnderZuoraMigration } from "helpers/subscriptions";
import {
  getBillingFrequency,
  getBillingProviderName,
  getSubscriptionCurrency,
} from "helpers/subscriptions";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { pluralizeString } from "helpers/utils";
import { UrlParamTypes } from "types/org-display";
import { SubscriptionDetailsProps } from "types/org-display";

const SubscriptionDetails = ({ refreshOrgData }: SubscriptionDetailsProps) => {
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const { copyToClipboard, showError } = useContext(SnackbarContext);

  const theme = useTheme();
  const classes = mainStyles();
  const [billingDetails, setBillingDetails] = useState<BillingDetails>();
  const [errorState, setErrorState] = useState(false);
  const [BDPillMode, setBDPillMode] = useState<PillStatusMode>(
    PillStatusMode.EDIT
  );

  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const { orgData } = useContext(OrgDataContext);
  const { subData } = useContext(SubDataContext);
  const { contractsList } = useContext(ContractsListContext);
  const { subscriptionSeatUsage } = useContext(SubscriptionSeatUsageContext);
  const { isOrgAndSeatDataLoading } = useContext(
    IsOrgAndSeatDataLoadingContext
  );
  const { defaultPaymentMethodDetails } = useContext(PaymentMethodsContext);
  const { isFeatureFlagsLoading } = useContext(FeatureFlagsContext);

  const canRetryPayment =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.RETRY_PAYMENT_COLLECTION]);

  const contract = (contractsList && contractsList?.contracts?.[0]) || null;

  const subscription = useMemo(() => {
    return orgData.subscription && orgData.subscription[0];
  }, [orgData]);

  const billingProviderName = getBillingProviderName(subscription);

  const currency = getSubscriptionCurrency(subscription);
  const billingFrequency = getBillingFrequency(subscription);

  const isOrgOnEnterprisePlan =
    contractsList && contractsList.contracts
      ? contractsList.contracts.some(
          (e) =>
            e?.status === ContractStatus.ACTIVATED && e?.hasEnterprise === true
        )
      : false;

  const orgTier =
    subscription && isOrgOnEnterprisePlan
      ? Tier.ENTERPRISE
      : subscription?.tier;

  const chargifyStateChipStyleMap = {
    [ChargifyState.UNSPECIFIED]: classes.neutral,
    [ChargifyState.ACTIVE]: classes.greenDay,
    [ChargifyState.TRIALING]: classes.blueMoon,
    [ChargifyState.PAST_DUE]: classes.yellowSubmarine,
    [ChargifyState.CANCELED]: classes.redWine,
  };
  const subscriptionTierChipStyleMap = {
    [Tier.PREMIUM]: classes.purpleRain,
    [Tier.ENTERPRISE]: classes.redWine,
  };
  const tesorioLink = `https://dashboard.tesorio.com/invoices/?customer_custom_fields__custentityroleid__string_icontains=${orgData.id}&ordering=-converted_balance&status__in=OPEN`;

  const isFreeOrg =
    subscription &&
    (subscription.type === SubscriptionType.FREE ||
      subscription.type === SubscriptionType.FREE_ORG);

  const getBillingDetails = useCallback(() => {
    getBillingDetailsApi(
      {
        orgId: toUUID(roleId),
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((response) => {
        setBillingDetails(response?.billingDetails);
        if (!response || !response.billingDetails) {
          setBDPillMode(PillStatusMode.CREATE);
        } else {
          setBDPillMode(PillStatusMode.EDIT);
        }
      })
      .catch((err) => {
        if (err.code === gRPCStatus.NOT_FOUND) {
          setBDPillMode(PillStatusMode.CREATE);
        } else {
          setBDPillMode(PillStatusMode.CHECK);
        }
        setErrorState(true);
      });
  }, [regionCode, roleId]);

  useEffect(() => {
    getBillingDetails();
  }, [getBillingDetails]);

  const openChargifyButton = () => {
    const chargifySiteCode = subscription?.chargify?.site;
    const chargifySubId = subscription?.chargify?.id;

    if (chargifySiteCode === undefined || chargifySubId === undefined) {
      return;
    }
    const subscriptionURL = `${mapChargifyURL(
      chargifySiteCode
    )}/subscriptions/${chargifySubId}`;

    return (
      <IconButton
        href={subscriptionURL}
        target="_blank"
        rel="noopener"
        size="small"
        color="primary"
        data-testid="open-cf-btn"
        style={{ position: "relative", bottom: 2 }}
      >
        <LaunchIcon fontSize="small" />
      </IconButton>
    );
  };

  const retryPaymentButton = () => {
    if (!canRetryPayment) {
      return;
    }
    if (
      subscription &&
      subscription.chargify &&
      (subscription.chargify.state !== ChargifyState.PAST_DUE ||
        subscription.chargify.paymentMethod !== PaymentMethod.AUTOMATIC ||
        defaultPaymentMethodDetails?.getType() === undefined)
    ) {
      return;
    }

    return (
      <Tooltip title="Retry payment">
        <IconButton
          onClick={() => {
            retryPayment();
          }}
          size="small"
          color="primary"
          data-testid="retry-payment-btn"
          style={{ position: "relative", bottom: 2 }}
        >
          <CurrencyExchangeIcon
            fontSize="small"
            color="primary"
            data-testid={"icon-retry-payment-button"}
          />
        </IconButton>
      </Tooltip>
    );
  };

  const taxExemptData = () => {
    if (subscription?.chargify?.taxExempt === undefined) {
      return NO_DISPLAY_DATA;
    }

    const taxExemptDisplay = (subscription?.chargify?.taxExempt).toString();
    return `${taxExemptDisplay.charAt(0).toUpperCase()}${taxExemptDisplay.slice(
      1
    )}`;
  };

  const getTotalSeats = (components: Components | undefined) => {
    let seatTotal = 0;
    if (components) {
      for (const [key, value] of Object.entries(components)) {
        if (value && key.match(/userSeats/)) {
          seatTotal = seatTotal + value;
        }
      }
    }

    return seatTotal;
  };

  const getSeatDisplay = (seatKey: string, value: number) => {
    if (isFreeOrg) {
      return (
        <Typography key={seatKey} align="left" variant="body2">
          <b>{userSeatsToString[seatKey]}:</b> {value}
        </Typography>
      );
    }

    let occupiedSeats = 0;
    let purchasedSeats = 0;
    const userSeat = userSeatsToString[seatKey];
    if (userSeat == "Full") {
      occupiedSeats =
        subscriptionSeatUsage?.premiumSeatUsageSummary?.occupiedSeats ?? 0;
      purchasedSeats =
        subscriptionSeatUsage?.premiumSeatUsageSummary?.purchasedSeats ?? 0;
    }
    if (userSeat == "Free") {
      occupiedSeats =
        subscriptionSeatUsage?.collaboratorSeatUsageSummary?.occupiedSeats ?? 0;
      purchasedSeats =
        subscriptionSeatUsage?.collaboratorSeatUsageSummary?.purchasedSeats ??
        0;
    }
    if (userSeat == "Lite") {
      occupiedSeats =
        subscriptionSeatUsage?.liteSeatUsageSummary?.occupiedSeats ?? 0;
      purchasedSeats =
        subscriptionSeatUsage?.liteSeatUsageSummary?.purchasedSeats ?? 0;
    }

    const dataAnchor = `subscription-seat-usage-${userSeatsToString[
      seatKey
    ].toLowerCase()}`;
    return (
      <Typography
        key={seatKey}
        align="left"
        variant="body2"
        data-testid={dataAnchor}
      >
        <>
          <span>
            <b>{userSeatsToString[seatKey]}:</b> {occupiedSeats} used /{" "}
            {purchasedSeats} {pluralizeString("seat", purchasedSeats)}
          </span>
        </>
      </Typography>
    );
  };

  const displaySeatComponents = () => {
    if (subscription && subscription.components) {
      const seatComponentEntries = [];

      // Populate paid team, paid seats
      for (const [key, value] of Object.entries(subscription.components)) {
        if (
          (value && key.match(/userSeatsPremium/)) ||
          (key.match(/userSeatsLite/) && !isFreeOrg)
        ) {
          seatComponentEntries.push([key, value]);
        }
      }

      // Paid team free seats
      if (!isFreeOrg) {
        seatComponentEntries.push([
          "userSeatsCollaborator",
          subscription.components.userSeatsCollaborator,
        ]);
      }

      // Free team seats
      if (isFreeOrg) {
        seatComponentEntries.push([
          "userSeatsFreeTeam",
          subscription.components.userSeatsFreeTeam,
        ]);
      }

      return seatComponentEntries.map(([key, value]) =>
        getSeatDisplay(key as string, value as number)
      );
    }
  };

  const getPeriodEnd = () => {
    if (
      subscription &&
      subscription.chargify &&
      subscription.chargify.currentPeriodEndsAt
    ) {
      if (subscription.chargify.cancelAtEndOfPeriod) {
        return (
          <Box
            component="span"
            sx={classes.secondaryText}
            data-testid="sub-cancel-at-period-end-display"
          >
            Set to cancel on{" "}
            {formatDateTimeDisplay(
              convertTimestampToDate(subscription.chargify.currentPeriodEndsAt)
            )}
          </Box>
        );
      }

      return formatDateTimeDisplay(
        convertTimestampToDate(subscription.chargify.currentPeriodEndsAt)
      );
    }

    return NO_DISPLAY_DATA;
  };

  const getPeriodStart = () => {
    if (
      subscription &&
      subscription.chargify &&
      subscription.chargify.currentPeriodStartedAt
    ) {
      return formatDateTimeDisplay(
        convertTimestampToDate(subscription.chargify.currentPeriodStartedAt)
      );
    }
    return NO_DISPLAY_DATA;
  };

  const displaySubscriptionTier = () => {
    if (isFreeOrg) {
      return null;
    } else if (subscription) {
      return (
        <TableRow data-testid="sub-tier-display">
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.leftCell25,
              classes.tableLeftColPadding
            )}
            align="right"
          >
            Plan
          </TableCell>
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.rightCell,
              classes.tableRightColPadding
            )}
            align="left"
          >
            <Chip
              size="small"
              sx={{
                fontWeight: "bold",
                ...subscriptionTierChipStyleMap[orgTier],
              }}
              label={subscriptionTierDisplayMapping[orgTier]}
            />
            {subscription && orgTier === Tier.ENTERPRISE && enterpriseTip()}
          </TableCell>
        </TableRow>
      );
    } else {
      return (
        <TableRow data-testid="sub-tier-na">
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.leftCell25,
              classes.tableLeftColPadding
            )}
            align="right"
          >
            Plan
          </TableCell>
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.rightCell,
              classes.tableRightColPadding
            )}
            align="left"
          >
            {NO_DISPLAY_DATA}
          </TableCell>
        </TableRow>
      );
    }
  };

  const isInvoiceCustomer = () => {
    if (
      subscription &&
      subscription.chargify &&
      subscription.chargify.paymentMethod !== undefined
    ) {
      return subscription.chargify.paymentMethod === PaymentMethod.INVOICE;
    } else {
      return false;
    }
  };

  const enterpriseTip = () => {
    const titleMessage = `Enterprise tier is calculated based on if org has any contract with status=activated and hasEnterprise=true. More info ${ENTERPRISE_PLAN_DOC_LINK}`;
    return (
      <Tooltip title={titleMessage}>
        <IconButton>
          <InfoIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const displayPeriodStartEndDate = () => {
    if (subscription && subscription.chargify) {
      return (
        <TableRow data-testid="sub-billing-period-start-end-date">
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.leftCell25,
              classes.tableLeftColPadding
            )}
            align="right"
          >
            Period Start-End
          </TableCell>
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.rightCell,
              classes.tableRightColPadding
            )}
            align="left"
          >
            <>
              {getPeriodStart()} {" - "} {getPeriodEnd()}
              <Tooltip
                title="Data from Subscription Document (Chargify)"
                placement="right"
                className={clsx(
                  classes.paddingTopXSmall,
                  classes.paddingBottomXSmall
                )}
              >
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          </TableCell>
        </TableRow>
      );
    }

    return null;
  };

  const displayChargifyDetails = () => {
    if (isFreeOrg) {
      return null;
    }
    if (
      subscription &&
      subscription.chargify &&
      subscription.chargify.site &&
      subscription.chargify.state
    ) {
      const isDecoupledSubscription =
        !isFreeOrg &&
        [ChargifyState.TRIALING, ChargifyState.CANCELED].includes(
          subscription.chargify.state
        ) &&
        !subscription.chargify.id;

      if (isDecoupledSubscription) {
        const decoupledTrialingMsg =
          "This organization is currently trialing and does not exist in " +
          billingProviderName +
          ". To unlock all subscription-related Periscope features, use the Convert Trial Subscription functionality in the Subscriptions tab.";
        const decoupledEndOfTrialMsg =
          "This organization has completed their trial. Reactivating this organization in the Subscription tab will create a " +
          billingProviderName +
          " subscription and unlock all subscription-related Periscope features.";
        return (
          <TableRow data-testid="sub-cf-details-display">
            <TableCell
              className={clsx(
                classes.tableCell,
                classes.leftCell25,
                classes.tableLeftColPadding
              )}
              align="right"
            >
              {billingProviderName} Details
            </TableCell>
            <TableCell
              className={clsx(
                classes.tableCell,
                classes.rightCell,
                classes.tableRightColPadding
              )}
              align="left"
            >
              <Tooltip
                data-testid="decoupled-trial-tool-tip"
                title={` ${
                  subscription.chargify.state === ChargifyState.TRIALING
                    ? decoupledTrialingMsg
                    : decoupledEndOfTrialMsg
                }`}
                className={clsx(
                  classes.paddingTopXSmall,
                  classes.paddingBottomXSmall
                )}
              >
                <Chip
                  size="small"
                  sx={{
                    fontWeight: "bold",
                    ...chargifyStateChipStyleMap[subscription.chargify.state],
                  }}
                  label={`${
                    chargifyStateDisplayMapping[subscription.chargify.state]
                  } (Decoupled)`}
                  icon={<InfoIcon style={{ color: "white" }} />}
                />
              </Tooltip>
            </TableCell>
          </TableRow>
        );
      }

      return (
        <TableRow data-testid="sub-cf-details-display">
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.leftCell25,
              classes.tableLeftColPadding
            )}
            align="right"
          >
            {billingProviderName} Details
          </TableCell>
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.rightCell,
              classes.tableRightColPadding
            )}
            align="left"
          >
            <Chip
              size="small"
              sx={{
                fontWeight: "bold",
                ...chargifyStateChipStyleMap[subscription.chargify.state],
              }}
              label={chargifyStateDisplayMapping[subscription.chargify.state]}
            />
            {" | "}
            <b>ID:</b> {subscription.chargify.id}
            {" | "}
            <b>Site:</b> {subscription.chargify.site} {openChargifyButton()}
            {retryPaymentButton()}
          </TableCell>
        </TableRow>
      );
    }
    return null;
  };

  const displayExternalAttributes = () => {
    let accountExecFullName = "";
    let csmFullName = "";

    if (contract && contract.metadata) {
      const metadata = contract.metadata;
      switch (metadata.details.case) {
        case "trial": {
          const accountExecutive = metadata.details.value.accountExecutive;
          const csm = metadata.details.value.customerSuccessManager;
          accountExecFullName = accountExecutive?.fullName || "";
          csmFullName = csm?.fullName || "";
          break;
        }
        case "nonTrial": {
          const accountExecutive = metadata.details.value.accountExecutive;
          const csm = metadata.details.value.customerSuccessManager;
          accountExecFullName = accountExecutive?.fullName || "";
          csmFullName = csm?.fullName || "";
          break;
        }
      }
    }

    return (
      <>
        {csmFullName && (
          <TableRow data-testid="sub-display-csm">
            <TableCell
              className={clsx(
                classes.tableCell,
                classes.leftCell25,
                classes.tableLeftColPadding
              )}
              align="right"
            >
              CSM
            </TableCell>
            <TableCell
              className={clsx(
                classes.tableCell,
                classes.rightCell,
                classes.tableRightColPadding
              )}
              align="left"
            >
              {csmFullName}
            </TableCell>
          </TableRow>
        )}

        {accountExecFullName && (
          <TableRow data-testid="sub-display-ae">
            <TableCell
              className={clsx(
                classes.tableCell,
                classes.leftCell25,
                classes.tableLeftColPadding
              )}
              align="right"
            >
              AE
            </TableCell>
            <TableCell
              className={clsx(
                classes.tableCell,
                classes.rightCell,
                classes.tableRightColPadding
              )}
              align="left"
            >
              {accountExecFullName}
              {isInvoiceCustomer() && (
                <>
                  {" "}
                  |{" "}
                  <Link
                    href={tesorioLink}
                    target="_blank"
                    underline="hover"
                    data-testid="tesorio-link"
                  >
                    Tesorio Link
                  </Link>
                </>
              )}
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  const displayBillingDetails = () => {
    return (
      <>
        <TableRow data-testid="billing-details-display">
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.leftCell25,
              classes.tableLeftColPadding
            )}
            align="right"
          >
            Billing Details
          </TableCell>
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.rightCell,
              classes.tableRightColPadding
            )}
            align="left"
          >
            <BillingDetailsForm
              billingDetails={billingDetails}
              statusMode={BDPillMode}
              refreshBillingDetails={() => getBillingDetails()}
            />
          </TableCell>
        </TableRow>
        {displayExternalAttributes()}
      </>
    );
  };

  const displayBillingExperiences = () => {
    if (!isFreeOrg) {
      const hasManualBillingExp = UseGetFeatureFlagValue(
        FEATURE_FLAG_KEY_MANUAL_BILLING_EXPERIENCE
      );

      return (
        <>
          <TableRow data-testid="manual-billing-experience-display">
            <TableCell
              className={clsx(
                classes.tableCell,
                classes.leftCell,
                classes.tableLeftColPadding
              )}
              align="right"
            >
              Manual Billing
            </TableCell>
            <TableCell
              className={clsx(
                classes.tableCell,
                classes.rightCell,
                classes.tableRightColPadding
              )}
              align="left"
            >
              {isFeatureFlagsLoading ? (
                <CircularProgress
                  color="primary"
                  variant="indeterminate"
                  thickness={4}
                  size={25}
                  data-testid="loading-icon"
                />
              ) : (
                <>
                  {hasManualBillingExp ? (
                    <Alert
                      severity="error"
                      sx={{ textAlign: "start" }}
                      data-testid="manual-billing-management-on-warning"
                    >
                      This customers billing is manually managed. Please contact
                      the finance team for billing inquiries.
                    </Alert>
                  ) : (
                    <>
                      Not applicable
                      <Tooltip
                        data-testid="manual-billing-management-tool-tip"
                        title="Manual billing is for selected customers who are billed under a different commercial model. Please contact the finance team for any questions."
                        className={clsx(
                          classes.paddingTopXSmall,
                          classes.paddingBottomXSmall
                        )}
                      >
                        <IconButton>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
            </TableCell>
          </TableRow>
        </>
      );
    }
  };

  const retryPayment = async () => {
    retryPaymentApi(
      {
        subscriptionId: subscription.id,
      },
      callOptionsHeaders(regionCode, orgData.id)
    )
      .then(() => {})
      .catch(showError);
  };

  const subscriptionDataDisplay = {
    tier:
      (subscription && subscriptionTierDisplayMapping[orgTier]) ||
      NO_DISPLAY_DATA,

    active: subscription?.active ? "Yes" : "No",

    type:
      (subscription && subscriptionTypeNameDisplayMapping[subscription.type]) ||
      NO_DISPLAY_DATA,

    totalSeats:
      subscription &&
      subscription.components &&
      getTotalSeats(subscription.components),

    id: (subscription && subscription.id) || NO_DISPLAY_DATA,
  };

  const displayPaymentDetails = () => {
    if (subscription) {
      return (
        <TableRow data-testid="sub-payment-detail-display">
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.leftCell25,
              classes.tableLeftColPadding
            )}
            align="right"
          >
            Payment Details
          </TableCell>
          <TableCell
            className={clsx(
              classes.tableCell,
              classes.rightCell,
              classes.tableRightColPadding
            )}
            align="left"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {subscription.chargify &&
                subscription.chargify.paymentMethod !== undefined && (
                  <span data-testid="sub-payment-method-display">
                    <b>Payment Method:</b>{" "}
                    {
                      subscriptionPaymentMethodDisplayMapping[
                        subscription.chargify.paymentMethod
                      ]
                    }
                  </span>
                )}
              {currency && (
                <span data-testid="sub-payment-details-display">
                  <b>Currency:</b> {currency}
                </span>
              )}
              {subscription.chargify &&
                subscription.chargify.taxExempt !== undefined && (
                  <span data-testid="sub-tax-exempt-display">
                    <b>Tax Exempt:</b> {taxExemptData()}
                  </span>
                )}
              {!isFreeOrg && (
                <span data-testid="sub-billing-cycle-display">
                  <b>Billing Cycle:</b>{" "}
                  {pricingPeriodDisplayMapping[billingFrequency]}
                  <Tooltip
                    title="Data from Subscription Document (Chargify)"
                    placement="right"
                    className={clsx(
                      classes.paddingTopXSmall,
                      classes.paddingBottomXSmall
                    )}
                  >
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              )}
            </Box>
          </TableCell>
        </TableRow>
      );
    }
  };
  return (
    <React.Fragment>
      <DisplayHeading style={{ paddingBottom: 10 }}>
        Subscription Details{" "}
        {isOrgAndSeatDataLoading ? (
          <CircularProgress
            color="primary"
            variant="indeterminate"
            thickness={4}
            size={20}
            data-testid="subscription-details-logo-loading"
            sx={{ verticalAlign: "middle" }}
          />
        ) : (
          <ChargifyLogoSvg size={30} />
        )}
      </DisplayHeading>

      {isUnderZuoraMigration(subData?.subscription?.zuoraMigrationStatus) && (
        <Alert severity="warning" sx={{ textAlign: "start", mb: 1 }}>
          Zuora billing migration in progress. Billing changes may be rejected
          or cause unexpected issues. Please avoid making billing changes unless
          absolutely necessary.
        </Alert>
      )}

      {!isOrgAndSeatDataLoading ? (
        <Box
          style={{
            borderColor: theme.palette.divider,
            borderWidth: "1px",
            borderStyle: "solid",
            borderRadius: "5px",
          }}
        >
          <Table size="small">
            <TableBody>
              {displaySubscriptionTier()}
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell25,
                    classes.tableLeftColPadding
                  )}
                  align="right"
                >
                  Active
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding
                  )}
                  align="left"
                >
                  {subscriptionDataDisplay.active}
                </TableCell>
              </TableRow>
              <TableRow data-testid="sub-total-sc-seats-display">
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell25,
                    classes.tableLeftColPadding
                  )}
                  align="right"
                  style={{ verticalAlign: "top" }}
                >
                  SC Total Seats
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding
                  )}
                  align="left"
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>{displaySeatComponents()}</div>
                  </Box>
                </TableCell>
              </TableRow>
              {!isFreeOrg ? (
                <TableRow data-testid="sub-total-cf-seats-display">
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.leftCell25,
                      classes.tableLeftColPadding
                    )}
                    align="right"
                    style={{ verticalAlign: "top" }}
                  >
                    {billingProviderName} Total Seats
                  </TableCell>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.rightCell,
                      classes.tableRightColPadding
                    )}
                    align="left"
                  >
                    <ChangeComponentQuantity
                      refreshOrgData={refreshOrgData}
                      subscription={subscription}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
              {displayPaymentDetails()}
              {displayPeriodStartEndDate()}
              <TableRow>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.leftCell25,
                    classes.tableLeftColPadding
                  )}
                  align="right"
                >
                  Sub ID
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.rightCell,
                    classes.tableRightColPadding
                  )}
                  align="left"
                >
                  <Typography variant="body2" align="left">
                    <span
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        copyToClipboard(subscriptionDataDisplay.id)
                      }
                    >
                      {subscriptionDataDisplay.id}{" "}
                      <Clipboard
                        color={theme.palette.text.primary}
                        size={"1rem"}
                        className="position-icon"
                      />
                    </span>
                    {subscription?.id && (
                      <>
                        {" "}
                        <RouterLink
                          to={{
                            pathname: `/search/doc/${subscription.id}`,
                          }}
                          target="_blank"
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <ExternalLink
                            color={theme.palette.text.primary}
                            size={"1rem"}
                            className="position-icon"
                          />
                        </RouterLink>
                      </>
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
              {displayChargifyDetails()}
              {displayBillingDetails()}
              {displayBillingExperiences()}
            </TableBody>
          </Table>
          <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            open={errorState}
            autoHideDuration={DEFAULT_SNACKBAR_DURATION}
            onClose={() => setErrorState(false)}
            data-testid="billing-details-toast-error"
          >
            <Alert severity="warning" variant="filled">
              Unable to retrieve billing details
            </Alert>
          </Snackbar>
        </Box>
      ) : (
        <>
          {Array(12)
            .fill(0)
            .map((_, index) => {
              return (
                <Grid container spacing={3} key={index}>
                  <Grid item xs={3}>
                    <Skeleton variant="text" height="30px" />
                  </Grid>
                  <Grid item xs={9}>
                    <Skeleton variant="text" height="30px" />
                  </Grid>
                </Grid>
              );
            })}
        </>
      )}
    </React.Fragment>
  );
};

export default SubscriptionDetails;
